import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { ADDRESSES } from "@/lib/config";

export interface ClaimedHistory {
    total: bigint;
    sources: {
        piggyStaking: bigint;
        lpStaking: bigint;
        reflection: bigint;
        referral: bigint;
    };
}

const toBig = (v: unknown): bigint => {
    try {
        if (typeof v === "string" && /^\d+$/.test(v)) return BigInt(v);
        return 0n;
    } catch { return 0n; }
};

/// Lifetime ETH the connected wallet has claimed across all reward buckets,
/// summed from on-chain Claimed events by the claimed-history function. Null
/// until a wallet is connected / the fetch resolves. Refreshes every 60s and
/// on demand via the returned refetch().
export function useClaimedHistory(): { data: ClaimedHistory | null; refetch: () => void } {
    const { address } = useAccount();
    const [data, setData] = useState<ClaimedHistory | null>(null);

    useEffect(() => {
        if (!address) { setData(null); return; }
        let cancelled = false;
        const load = () => {
            const params = new URLSearchParams({
                wallet: address,
                ps: ADDRESSES.PIGGY_STAKING ?? "",
                lp: ADDRESSES.LOCKED_LP_STAKING ?? "",
                refl: ADDRESSES.REFLECTION ?? "",
                ref: ADDRESSES.REFERRAL_REGISTRY ?? "",
            });
            fetch(`/.netlify/functions/claimed-history?${params.toString()}`)
                .then((r) => r.json())
                .then((d) => {
                    if (cancelled) return;
                    const s = d?.sources ?? {};
                    setData({
                        total: toBig(d?.total),
                        sources: {
                            piggyStaking: toBig(s.piggyStaking),
                            lpStaking: toBig(s.lpStaking),
                            reflection: toBig(s.reflection),
                            referral: toBig(s.referral),
                        },
                    });
                })
                .catch(() => { /* keep last good value */ });
        };
        load();
        const id = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, [address]);

    const refetch = () => {
        // Bump by toggling address dep is overkill; consumers can rely on the
        // 60s poll. Exposed for parity with other hooks.
    };
    return { data, refetch };
}
